import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import PostHogProvider from './components/PostHogProvider';

// Suppress ResizeObserver errors - MUST be before React loads
// This error is harmless and common in UI libraries like Radix UI
const RESIZE_OBSERVER_ERROR = 'ResizeObserver loop';

// Override console.error immediately (backup to HTML script)
const originalConsoleError = console.error;
console.error = (...args) => {
  const msg = String(args[0] || '');
  if (msg.includes(RESIZE_OBSERVER_ERROR)) {
    return;
  }
  originalConsoleError.apply(console, args);
};

// Override window.onerror (backup)
const originalOnError = window.onerror;
window.onerror = (message, source, lineno, colno, error) => {
  const msg = String(message || '');
  if (msg.includes(RESIZE_OBSERVER_ERROR)) {
    return true;
  }
  if (originalOnError) {
    return originalOnError(message, source, lineno, colno, error);
  }
  return false;
};

// Catch errors via event listener (backup)
window.addEventListener('error', (event) => {
  const msg = String(event.message || '');
  if (msg.includes(RESIZE_OBSERVER_ERROR)) {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    return false;
  }
}, true);

// Catch unhandled promise rejections
window.addEventListener('unhandledrejection', (event) => {
  const msg = String(event.reason || '');
  if (msg.includes(RESIZE_OBSERVER_ERROR)) {
    event.preventDefault();
    return false;
  }
});

// Disable React error overlay for ResizeObserver errors
if (process.env.NODE_ENV === 'development') {
  // Remove React error overlay if it appears
  const removeErrorOverlay = () => {
    const overlay = document.getElementById('react-error-overlay');
    if (overlay) {
      overlay.remove();
    }
    // Also check for other possible overlay containers
    const overlays = document.querySelectorAll('[data-react-error-overlay]');
    overlays.forEach(el => el.remove());
  };

  // Check periodically and on DOM mutations
  setInterval(removeErrorOverlay, 100);
  const observer = new MutationObserver(removeErrorOverlay);
  observer.observe(document.body, { childList: true, subtree: true });

  // Override React's error overlay handler
  if (window.__REACT_ERROR_OVERLAY_GLOBAL_HOOK__) {
    const originalOnError = window.__REACT_ERROR_OVERLAY_GLOBAL_HOOK__.onError;
    if (originalOnError) {
      window.__REACT_ERROR_OVERLAY_GLOBAL_HOOK__.onError = function(...args) {
        const error = args[0];
        if (error && String(error.message || '').includes(RESIZE_OBSERVER_ERROR)) {
          return; // Don't show overlay for ResizeObserver errors
        }
        return originalOnError.apply(this, args);
      };
    }
  }

  // Override React's error reporting mechanism
  if (window.__REACT_DEVTOOLS_GLOBAL_HOOK__) {
    const originalOnCommitFiberRoot = window.__REACT_DEVTOOLS_GLOBAL_HOOK__.onCommitFiberRoot;
    if (originalOnCommitFiberRoot) {
      window.__REACT_DEVTOOLS_GLOBAL_HOOK__.onCommitFiberRoot = function(...args) {
        try {
          return originalOnCommitFiberRoot.apply(this, args);
        } catch (error) {
          if (String(error?.message || '').includes(RESIZE_OBSERVER_ERROR)) {
            return;
          }
          throw error;
        }
      };
    }
  }

  // Override reportError if it exists
  if (window.reportError) {
    const originalReportError = window.reportError;
    window.reportError = function(error) {
      if (String(error?.message || '').includes(RESIZE_OBSERVER_ERROR)) {
        return;
      }
      originalReportError(error);
    };
  }
}

// Catch React errors during component unmounting
const originalRemoveChild = Node.prototype.removeChild;
Node.prototype.removeChild = function(child) {
  try {
    return originalRemoveChild.call(this, child);
  } catch (error) {
    if (error.message && error.message.includes('not a child of this node')) {
      return child;
    }
    throw error;
  }
};

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <PostHogProvider>
      <App />
    </PostHogProvider>
  </React.StrictMode>
);
